import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt, FaBuilding, FaEnvelope, FaMapMarkerAlt, FaPlus, FaArrowLeft } from 'react-icons/fa';

export default function Login() {
  const [isSignup, setIsSignup] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Signup States
  const [formData, setFormData] = useState({ name: '', email: '', location: '' });
  
  // Login States
  const [selectedSocietyId, setSelectedSocietyId] = useState('');
  const [loginEmail, setLoginEmail] = useState('');

  useEffect(() => {
    fetchSocieties();
  }, []);

  const fetchSocieties = async () => {
    const { data } = await supabase.from('societies').select('*').order('id', { ascending: true });
    setSocieties(data || []);
  };

  // --- 1. HANDLE SIGNUP (SOCIETY TABLE ENTRY) ---
  const handleCreateSociety = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const { data, error } = await supabase
      .from('societies')
      .insert([{ 
        name: formData.name.toUpperCase(), 
        email: formData.email.toLowerCase(), 
        location: formData.location.toUpperCase() 
      }])
      .select();

    if (error) {
      alert("DATABASE_ERROR: " + error.message);
    } else {
      alert(`SUCCESS: SOCIETY_ID_${data[0].id} CREATED`);
      setIsSignup(false);
      setFormData({ name: '', email: '', location: '' }); // Clear form
      fetchSocieties();
    }
    setLoading(false);
  };

  // --- 2. HANDLE LOGIN (SOCIETY TABLE VERIFICATION) ---
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedSocietyId || !loginEmail) return alert("REQUIRED: SOCIETY_ID & EMAIL");

    setLoading(true);
    try {
      // Direct validation from societies table
      const { data, error } = await supabase
        .from('societies')
        .select('*')
        .eq('id', selectedSocietyId)
        .eq('email', loginEmail.trim().toLowerCase())
        .single();

      if (error || !data) {
        alert("ACCESS_DENIED: INVALID_CREDENTIALS");
      } else {
        console.log("LOGIN_SUCCESS:", data);
        localStorage.setItem('activeSocietyId', data.id);
        localStorage.setItem('activeSocietyName', data.name);
        
        // Go to the society welcome page
        navigate(`/society/${data.id}`);
      }
    } catch (err) {
      alert("SYSTEM_CRITICAL_ERROR");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.glassCard}>
        <div style={styles.logoHeader}>
          <FaShieldAlt style={styles.mainIcon} />
          <h1 style={styles.brandName}>EF_ERP_SYSTEM</h1>
          <p style={styles.tagline}>{isSignup ? '// REGISTER_NEW_SOCIETY' : '// SECURE_ACCESS_PORTAL'}</p>
        </div>

        {isSignup ? (
          <form onSubmit={handleCreateSociety} style={styles.form}>
            <div style={styles.inputWrapper}>
              <FaBuilding style={styles.fieldIcon} />
              <input 
                style={styles.inputField} 
                placeholder="FULL SOCIETY NAME" 
                onChange={e => setFormData({...formData, name: e.target.value})} 
                required 
              />
            </div>
            <div style={styles.inputWrapper}>
              <FaEnvelope style={styles.fieldIcon} />
              <input 
                style={styles.inputField} 
                type="email" 
                placeholder="OFFICIAL EMAIL" 
                onChange={e => setFormData({...formData, email: e.target.value})} 
                required 
              />
            </div>
            <div style={styles.inputWrapper}>
              <FaMapMarkerAlt style={styles.fieldIcon} />
              <input 
                style={styles.inputField} 
                placeholder="LOCATION (CITY)" 
                onChange={e => setFormData({...formData, location: e.target.value})} 
                required 
              />
            </div>
            <button type="submit" disabled={loading} style={styles.actionBtn}>
              {loading ? "INITIALIZING_DB..." : "CREATE_SOCIETY_PROFILE"}
            </button>
            <p onClick={() => setIsSignup(false)} style={styles.switchLink}>
              <FaArrowLeft /> BACK_TO_LOGIN
            </p>
          </form>
        ) : (
          <form onSubmit={handleLogin} style={styles.form}>
            <div style={styles.inputWrapper}>
              <FaBuilding style={styles.fieldIcon} />
              <select 
                style={styles.selectField} 
                value={selectedSocietyId}
                onChange={e => setSelectedSocietyId(e.target.value)}
                required
              >
                <option value="">-- CHOOSE_SOCIETY --</option>
                {societies.map(s => (
                  <option key={s.id} value={s.id}>ID_{s.id} | {s.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.inputWrapper}>
              <FaEnvelope style={styles.fieldIcon} />
              <input 
                style={styles.inputField} 
                type="email" 
                placeholder="REGISTERED_EMAIL" 
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                required 
              />
            </div>
            <button type="submit" disabled={loading} style={styles.actionBtn}>
              {loading ? "AUTHENTICATING..." : "AUTH_&_ENTER →"}
            </button>
            <div style={styles.divider}>OR</div>
            <button type="button" onClick={() => setIsSignup(true)} style={styles.signupBtn}>
              <FaPlus /> REGISTER_SOCIETY_UNIT
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { background: '#02040a', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: "monospace", color: '#fff' },
  glassCard: { background: 'rgba(255, 255, 255, 0.02)', border: '1px solid #00e0ff33', padding: '40px', borderRadius: '4px', width: '100%', maxWidth: '420px', backdropFilter: 'blur(10px)', boxShadow: '0 0 30px rgba(0, 224, 255, 0.05)' },
  logoHeader: { textAlign: 'center', marginBottom: '30px' },
  mainIcon: { fontSize: '40px', color: '#00e0ff', marginBottom: '10px' },
  brandName: { color: '#fff', margin: 0, letterSpacing: '4px', fontSize: '20px' },
  tagline: { color: '#00e0ff', fontSize: '10px', opacity: 0.6, letterSpacing: '2px', marginTop: '10px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  fieldIcon: { position: 'absolute', left: '15px', color: '#00e0ff', fontSize: '14px', zIndex: 1 },
  inputField: { width: '100%', background: '#000', border: '1px solid #222', padding: '15px 15px 15px 45px', color: '#fff', outline: 'none', fontSize: '13px' },
  selectField: { width: '100%', background: '#000', border: '1px solid #222', padding: '15px 15px 15px 45px', color: '#fff', outline: 'none', cursor: 'pointer', appearance: 'none' },
  actionBtn: { background: '#00e0ff', color: '#000', padding: '16px', border: 'none', fontWeight: 'bold', cursor: 'pointer', letterSpacing: '1px', transition: '0.3s' },
  signupBtn: { background: 'transparent', color: '#00e0ff', padding: '14px', border: '1px solid #00e0ff', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  divider: { textAlign: 'center', fontSize: '10px', color: '#444' },
  switchLink: { textAlign: 'center', fontSize: '11px', color: '#00e0ff', cursor: 'pointer', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }
};